/**
 * OAuth Callback Page - v3
 * 
 * Handles the callback from Stytch after Google authentication.
 * Authenticates the token and redirects back to ChatGPT.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StytchProvider, useStytch } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';

// Stytch configuration
const STYTCH_PUBLIC_TOKEN = 'public-token-live-93829aa9-3610-423a-969e-12e3039a3b44';

// Create the Stytch client on first use, not at import: App.tsx imports this page
// for its route, so a module-level client ran Stytch's bootstrap, session and
// RBAC requests on every page of the app for every visitor (and their failures
// were TrackJS's "[Stytch] Unable to retrieve RBAC policy" / "Unable to contact
// our servers" reports). It is only needed here.
let stytchClient: StytchUIClient | null = null;
function getStytchClient(): StytchUIClient {
  if (!stytchClient) stytchClient = new StytchUIClient(STYTCH_PUBLIC_TOKEN);
  return stytchClient;
}

function OAuthCallbackContent() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'error' | 'success'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const stytch = useStytch();

  const handleCallback = useCallback(async () => {
    try {
      // Get the token from Stytch callback
      const token = searchParams.get('token');
      const stytchTokenType = searchParams.get('stytch_token_type');
      
      console.log('[OAuth Callback] Received:', { 
        token: token ? token.substring(0, 20) + '...' : 'none', 
        stytchTokenType 
      });

      if (!token) {
        throw new Error('No token received from authentication');
      }

      // Get stored OAuth params
      const storedParams = sessionStorage.getItem('oauth_params');
      if (!storedParams) {
        throw new Error('OAuth session expired. Please try again.');
      }

      const oauthParams = JSON.parse(storedParams);
      console.log('[OAuth Callback] Stored params:', {
        redirectUri: oauthParams.redirectUri,
        state: oauthParams.state,
      });

      // Authenticate the OAuth token with Stytch to create a session
      console.log('[OAuth Callback] Authenticating with Stytch...');
      const authResponse = await stytch.oauth.authenticate(token, {
        session_duration_minutes: 60,
      });
      
      console.log('[OAuth Callback] Auth successful, user:', authResponse.user?.emails?.[0]?.email);

      // Get the session JWT to use as the authorization code
      // ChatGPT will exchange this via our token endpoint
      const sessionJwt = authResponse.session_jwt;
      
      if (!sessionJwt) {
        throw new Error('No session token received');
      }

      // Redirect back to ChatGPT with the session JWT as the authorization code
      const redirectUrl = new URL(oauthParams.redirectUri);
      redirectUrl.searchParams.set('code', sessionJwt);
      redirectUrl.searchParams.set('state', oauthParams.state);

      console.log('[OAuth Callback] Redirecting to ChatGPT');
      
      // Clear stored params
      sessionStorage.removeItem('oauth_params');
      
      setStatus('success');
      
      // Redirect back to ChatGPT
      setTimeout(() => {
        window.location.href = redirectUrl.toString();
      }, 500);

    } catch (error) {
      console.error('[OAuth Callback] Error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Authentication failed');
    }
  }, [searchParams, stytch]);

  useEffect(() => {
    // Small delay to ensure Stytch is ready
    const timer = setTimeout(() => {
      handleCallback();
    }, 100);
    return () => clearTimeout(timer);
  }, [handleCallback]);

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Authentication Error</h1>
          <p className="text-text-secondary mb-6">{errorMessage}</p>
          <a
            href="/oauth"
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
          >
            Try Again
          </a>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl max-w-md w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Connected!</h1>
          <p className="text-text-secondary">Redirecting back to ChatGPT...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/20 rounded-full blur-3xl" />
      </div>

      <div className="relative bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl max-w-md w-full text-center">
        <img src="/logo.svg" alt="Vivideo" className="w-16 h-16 mx-auto mb-4 shadow-glow rounded-2xl" />
        <h1 className="text-xl font-bold text-text-primary mb-2">Completing Sign In</h1>
        <p className="text-text-secondary mb-6">Please wait...</p>
        
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

export function OAuthCallbackPage() {
  return (
    <StytchProvider stytch={getStytchClient()}>
      <OAuthCallbackContent />
    </StytchProvider>
  );
}

